import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ApiError, apiGet } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import type { Roadmap } from "../../types";

const STATUS_LABEL: Record<string, string> = {
  complete: "✓ Previously passed",
  in_progress: "In Progress",
  available: "Start →",
  locked: "🔒 Locked",
};

export function RoadmapPage() {
  const { cid } = useParams<{ cid: string }>();
  const { user } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ["roadmap", cid, user?.id],
    queryFn: () => apiGet<Roadmap>(`/api/curricula/${cid}/roadmap`),
    retry: false,
  });

  if (isLoading) return <p className="text-muted">Loading learning pathway...</p>;
  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="subjects">
        <div className="subjects-empty">
          <p className="subjects-empty-title">This subject isn't active on your account</p>
          <p className="subjects-empty-body">
            You are not currently enrolled in this course module. You can enrol via the
            catalogue to start guided dialogues.
          </p>
          <Link to="/browse" className="btn btn-primary">
            Browse Catalogue →
          </Link>
        </div>
      </div>
    );
  }
  if (!data) return <p>Curriculum not found.</p>;

  const completed = data.lessons.filter((l) => l.status === "complete").length;
  const total = data.lessons.length;

  return (
    <div className="roadmap-page">
      <div className="roadmap-breadcrumb-bar">
        <Link to="/learn" className="breadcrumb-link">
          ← My Subjects
        </Link>
      </div>

      <header className="roadmap-header">
        <h1>{data.curriculum_title}</h1>
        <p className="roadmap-desc">
          Structured learning roadmap. Complete each guided dialogue and demonstrate understanding
          of the core competencies to advance.
        </p>
      </header>

      {/* Metrics */}
      <div className="roadmap-metrics-grid">
        <div className="stat-card">
          <span className="stat-value">{data.mastery_pct}%</span>
          <span className="stat-label">Current Mastery</span>
        </div>

        {data.quiz_pct != null && (
          <div className="stat-card">
            <span className="stat-value">{data.quiz_pct}%</span>
            <span className="stat-label">Quiz Score</span>
            <span className="stat-subtext">Latest quiz per lesson, not part of mastery</span>
          </div>
        )}

        <div className="stat-card">
          <span className="stat-value">
            {completed}/{total}
          </span>
          <span className="stat-label">Lessons Passed</span>
          <span className="stat-subtext">
            {total > 0 ? Math.round((completed / total) * 100) : 0}% Course Completion
          </span>
        </div>

        <div className="stat-card">
          <span className="stat-value">
            {data.lessons.reduce((n, l) => n + l.objective_count, 0)}
          </span>
          <span className="stat-label">Total Objectives</span>
        </div>
      </div>

      <p className="progress-measure-note">
        Mastery is the percentage of assessed objectives currently mastered. A lesson stays passed
        after a successful attempt, even if later scores are lower.
      </p>

      {/* Progress Track */}
      <div className="progress-track-wrapper">
        <div className="progress-track-meta">
          <span>Overall Course Progress</span>
          <span>{completed} of {total} Units Completed</span>
        </div>
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Visual Learning Path (Nodes) */}
      <div className="learning-pathway">
        <div className="pathway-stem-line" aria-hidden="true" />
        <ol className="pathway-list">
          {data.lessons.map((l) => {
            const isCompleted = l.status === "complete";
            const isInProgress = l.status === "in_progress";
            const normalizedScore = l.score > 1 ? Math.round(l.score) : Math.round(l.score * 100);

            return (
              <li
                key={l.lesson_id}
                className={`pathway-node status-${l.status} ${isCompleted ? "is-passed" : ""} ${isInProgress ? "is-active" : ""}`}
              >
                {/* Node Milestone Pin */}
                <div className="pathway-pin" aria-hidden="true">
                  {isCompleted ? "✓" : l.order}
                </div>

                <div className="pathway-card">
                  <div className="pathway-main">
                    <div className="pathway-top-meta">
                      <span className="pathway-order-label">Unit {l.order}</span>
                      <span aria-hidden="true">·</span>
                      <span className="pathway-diff-label">{l.difficulty}</span>
                      <span aria-hidden="true">·</span>
                      <span className={`roadmap-status badge badge-${l.status}`}>
                        {STATUS_LABEL[l.status] ?? l.status}
                      </span>
                      {l.attempts > 0 && (
                        <>
                          <span aria-hidden="true">·</span>
                          <span className="pathway-mark-label">
                            Best: <strong>{normalizedScore}%</strong>
                          </span>
                        </>
                      )}
                      {l.quiz_score != null && (
                        <>
                          <span aria-hidden="true">·</span>
                          <span className="pathway-mark-label">
                            Last quiz: <strong>{l.quiz_score}%</strong>
                          </span>
                        </>
                      )}
                    </div>

                    <h2 className="pathway-title">{l.title}</h2>

                    <div className="pathway-objectives-meta">
                      <span>{l.objective_count} Assessed Objectives</span>
                      {l.prerequisites && l.prerequisites.length > 0 && (
                        <span> · Requires: {l.prerequisites.join(", ")}</span>
                      )}
                    </div>
                  </div>

                  <div className="pathway-actions">
                    {isCompleted ? (
                      <div className="pathway-actions-group">
                        <Link
                          to={`/learn/${cid}/lesson/${l.lesson_id}`}
                          className="btn btn-sm btn-ghost"
                        >
                          Review Lesson
                        </Link>
                        <Link
                          to={`/learn/${cid}/dialog/${l.lesson_id}`}
                          className="btn btn-sm btn-secondary"
                        >
                          Revise Dialogue
                        </Link>
                        <Link
                          to={`/learn/${cid}/quiz/${l.lesson_id}`}
                          className="btn btn-sm btn-ghost"
                        >
                          Self Quiz
                        </Link>
                      </div>
                    ) : (
                      <div className="pathway-actions-group">
                        <Link
                          to={`/learn/${cid}/lesson/${l.lesson_id}`}
                          className="btn btn-sm btn-primary"
                        >
                          Study &amp; Explain →
                        </Link>
                        <Link
                          to={`/learn/${cid}/quiz/${l.lesson_id}`}
                          className="btn btn-sm btn-secondary"
                        >
                          Quiz
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
